import * as React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';

import { useGetZone } from 'src/features/zone/api/useGetZone';
import ZonePricingPlans from 'src/features/zone/components/ZonePricingPlans';
import { BOOLEAN_MAP } from 'src/features/sites/utils/constants';
import { VISITOR_CATEGORIES_TRANSLATION_MAP, ZONE_TRANSLATION_MAP } from 'src/features/zone/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { zoneMessages } from 'src/features/zone/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import { Separator } from 'src/registry/ui/Separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useParking } from 'src/providers/ParkingProvider';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { generalMessages } from 'src/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import BackButton from 'src/features/navigation/components/BackButton';

type BOOLEAN_TYPE = 'true' | 'false';
export default function ZoneDashboard() {
    const { formatMessage, locale } = useIntl();
    const { zoneId = '' } = useParams();
    const { parkingId } = useParking();

    const { data: zoneData } = useGetZone({ zoneId, parkingId });

    const accessSites = useHasPermissions([PERMISSION.ACCESS_SITES]);
    const managePricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);

    const [searchParams, setSearchParams] = useSearchParams();

    const defaultTab = 'pricing-plans';
    const tab = searchParams.get('tab') ?? defaultTab;

    const { setTitle, removeTitle } = useTitleStore();
    React.useEffect(() => {
        if (!zoneData) return;
        setTitle(zoneData.translations.find((translation) => translation.language === locale)?.name ?? '');
        return removeTitle;
    }, [locale, removeTitle, setTitle, zoneData]);

    return (
        <>
            <BackButton />
            <GeneralBlock>
                <div className="grid flex-3 grid-cols-3 gap-4">
                    {zoneData?.translations.map((translation) => {
                        const languageToUpper = translation.language.toUpperCase();
                        return (
                            <GeneralBlockAttribute
                                label={`${formatMessage(generalMessages.name)} (${languageToUpper})`}
                                value={translation.name}
                                key={translation.id}
                            />
                        );
                    })}
                </div>
                <Separator />
                <div className="grid flex-3 grid-cols-3 gap-4">
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.type)}
                        value={zoneData ? formatMessage(ZONE_TRANSLATION_MAP[zoneData?.type]) : '-'}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.visitorCategories)}
                        value={
                            zoneData?.visitor_categories?.length
                                ? zoneData?.visitor_categories
                                      ?.map((c) => formatMessage(VISITOR_CATEGORIES_TRANSLATION_MAP[c]))
                                      .join(', ')
                                : '-'
                        }
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.spacesCount)}
                        value={zoneData?.spaces_count}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.buffer)}
                        value={zoneData?.overflow_spaces}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.totalSpaces)}
                        value={zoneData?.total_spaces}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.site)}
                        value={
                            zoneData
                                ? zoneData.site.translations.find((translation) => translation.language === locale)
                                      ?.name
                                : '-'
                        }
                        link={accessSites && zoneData ? `/${PATH.ADMINISTRATION}/site/${zoneData?.site.id}` : undefined}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.organisation)}
                        value={zoneData?.service_provider.name}
                    />
                    {zoneData?.parent_zone ? (
                        zoneData.parent_zone.translations.map((translation) => {
                            const languageToUpper = translation.language.toUpperCase();
                            return (
                                <GeneralBlockAttribute
                                    label={`${formatMessage(zoneMessages.parentZone)} (${languageToUpper})`}
                                    value={translation.name}
                                    link={`/${PATH.ADMINISTRATION}/zone/${zoneData.parent_zone.id}`}
                                    key={translation.id}
                                />
                            );
                        })
                    ) : (
                        <GeneralBlockAttribute label={formatMessage(zoneMessages.parentZone)} />
                    )}
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.limitAccessCapacity)}
                        value={
                            zoneData
                                ? formatMessage(
                                      BOOLEAN_MAP[zoneData.limit_access_per_capacity.toString() as BOOLEAN_TYPE],
                                  )
                                : '-'
                        }
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.limitUserAccess)}
                        value={
                            zoneData
                                ? formatMessage(BOOLEAN_MAP[zoneData.limit_user_access.toString() as BOOLEAN_TYPE])
                                : '-'
                        }
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.userAccess)}
                        value={zoneData?.user_access.length ? zoneData?.user_access.map((z) => z.name).join(', ') : '-'}
                    />
                </div>
                <Separator />
                <div className="grid flex-3 grid-cols-3 gap-4">
                    {zoneData?.translations.map((translation) => {
                        const languageToUpper = translation.language.toUpperCase();
                        return (
                            <GeneralBlockAttribute
                                key={translation.id}
                                label={`${formatMessage(generalMessages.description)} (${languageToUpper})`}
                                value={translation.description.length ? translation.description : '-'}
                            />
                        );
                    })}
                </div>
            </GeneralBlock>
            {managePricingPlans && (
                <Tabs
                    defaultValue={defaultTab}
                    value={tab}
                    onValueChange={(value) => setSearchParams({ tab: value }, { replace: true })}
                >
                    <TabsList>
                        <TabsTrigger value="pricing-plans">
                            <FormattedMessage {...pricingPlanMessages.pricingPlans} />
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="pricing-plans">
                        <ZonePricingPlans zoneId={zoneId} />
                    </TabsContent>
                </Tabs>
            )}
        </>
    );
}
